import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { messageSendsApi, templateApi } from '../services/api';
import { SendStatsRow, WhatsAppTemplate } from '../types';
import { pct } from '../lib';
import { Dash, Empty, Failed, Loading, PageFrame, Pill, Section, Tabs, Th, td, trClick } from '../components/PlatformBits';
import { Rendered, renderTemplate, shapeLabel, thumbOf } from '../components/WhatsAppPreview';

/** Templates — every lead-facing template as one row: its picture, its
 *  shape, and its ledger numbers, visits first. A row opens its sends. */

type Sort = 'visits' | 'replies' | 'sends' | 'newest';
const num = (n: number) => n.toLocaleString('en-IN');

const TemplatesPage: React.FC = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<WhatsAppTemplate[] | null>(null);
  const [stats, setStats] = useState<SendStatsRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [group, setGroup] = useState('');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<Sort>('visits');

  useEffect(() => {
    Promise.all([templateApi.list(), messageSendsApi.stats('template')])
      .then(([t, s]) => { setTemplates(t); setStats(s); })
      .catch((err) => setError(err?.response?.data?.detail || err.message));
  }, []);

  const byId = useMemo(() => Object.fromEntries((templates || []).map((t) => [t.id, t])), [templates]);
  const statsById = useMemo(() => Object.fromEntries((stats || []).map((r) => [r.key || '', r])), [stats]);
  // Lead-facing, not retired — unless the ledger has sends for it (a retired template still has a past).
  const marketing = useMemo(
    () => (templates || []).filter((t) => t.category !== 'UTILITY' && (!t.retired_at || statsById[t.id]?.sends)),
    [templates, statsById],
  );
  const groups = useMemo(() => Array.from(new Set(marketing.map((t) => t.group).filter((g): g is string => !!g))).sort(), [marketing]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const n = (t: WhatsAppTemplate) => statsById[t.id];
    const key = (t: WhatsAppTemplate): number[] => {
      const s = n(t);
      if (sort === 'visits') return [s?.visited || 0, s?.replied || 0, s?.sends || 0];
      if (sort === 'replies') return [s?.replied || 0, s?.sends || 0];
      return [s?.sends || 0];
    };
    return marketing
      .filter((t) => !group || t.group === group)
      .filter((t) => !q || t.name.includes(q) || t.display_name.toLowerCase().includes(q))
      .sort((a, b) => {
        if (sort === 'newest') return b.created_at.localeCompare(a.created_at);
        const ka = key(a); const kb = key(b);
        for (let i = 0; i < ka.length; i++) if (kb[i] !== ka[i]) return kb[i] - ka[i];
        return b.created_at.localeCompare(a.created_at);
      });
  }, [marketing, group, query, sort, statsById]);

  if (error) return <Failed what="templates" error={error} />;

  const groupTabs: [string, React.ReactNode][] = [['', 'All'], ...groups.map((g): [string, React.ReactNode] => [g, g])];

  return (
    <PageFrame title="Templates" subline="Every lead-facing template and what its sends led to — open one for its leads.">
      {!templates ? <Loading what="templates" /> : (
        <Section title={`${rows.length} template${rows.length === 1 ? '' : 's'}`}
                 note="visits booked within 14 days · replies within 7 days of a send">
          <div className="flex items-center gap-3 flex-wrap px-1 py-3">
            <div className="relative">
              <svg viewBox="0 0 16 16" className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-graphite pointer-events-none" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
                <circle cx="7" cy="7" r="4.5" /><path d="M10.5 10.5 14 14" strokeLinecap="round" />
              </svg>
              <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search templates"
                     className="h-8 pl-8 pr-3 w-[240px] rounded-lg border border-charcoal/15 bg-warm-white text-[13px] focus:outline-none focus:border-forest focus:ring-2 focus:ring-sage/40" />
            </div>
            {groups.length > 0 && <Tabs value={group} onChange={setGroup} items={groupTabs} />}
            <span className="flex-1" />
            <span className="text-[11px] uppercase tracking-[0.06em] font-semibold text-graphite">Sort</span>
            <Tabs value={sort} onChange={setSort} items={[['visits', 'Visits'], ['replies', 'Replies'], ['sends', 'Sends'], ['newest', 'Newest']]} />
          </div>
          <table className="w-full min-w-[860px] border-collapse">
            <thead><tr>
              <Th left>Template</Th><Th ours>Visits booked</Th><Th ours>Replied</Th><Th>Sent</Th><Th>Delivered</Th><Th>Read</Th>
              <th className="w-6 border-b border-charcoal/10" />
            </tr></thead>
            <tbody>
              {rows.length === 0 && <Empty cols={7}>No templates match{query ? ` “${query}”` : ''}.</Empty>}
              {rows.map((t) => {
                const r = renderTemplate(t, t.parent_id ? byId[t.parent_id] : undefined);
                const n = statsById[t.id];
                const draft = t.status !== 'APPROVED';
                const sent = !!n && n.sends > 0;
                return (
                  <tr key={t.id} onClick={() => navigate(`/templates/${t.id}`)} className={`${trClick(false)} group !align-middle`}>
                    <td className="px-3 py-2.5">
                      <div className={`flex items-center gap-3.5 min-w-0 ${draft || t.retired_at ? 'opacity-55' : ''}`}>
                        <Thumb r={r} />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-[14px] text-charcoal group-hover:text-forest">{t.display_name}</span>
                            {draft && <Pill tone="gold">{t.status.toLowerCase()}</Pill>}
                            {t.retired_at && <Pill>retired</Pill>}
                            {t.group && !group && <span className="text-[11px] text-graphite">{t.group}</span>}
                          </div>
                          <div className="text-[12px] text-graphite mt-0.5 truncate max-w-[440px]">
                            {t.parent_id
                              ? <>fills <span className="text-forest font-semibold">{byId[t.parent_id]?.name || t.parent_id}</span>{r ? ` · ${shapeLabel(r)}` : ''}</>
                              : r ? shapeLabel(r) : t.name}
                          </div>
                        </div>
                      </div>
                    </td>
                    {sent ? (
                      <>
                        <td className={`${td} text-right`}>
                          {n.visited ? <span className="text-forest font-semibold text-[15px]">{num(n.visited)}</span> : <Dash />}
                        </td>
                        <td className={`${td} text-right`}>
                          <span className="font-semibold text-[14px] text-amber-ink">{num(n.replied)}</span>
                          <span className="text-[11.5px] text-graphite ml-1">{pct(n.replied, n.sends)}</span>
                        </td>
                        <td className={`${td} text-right font-semibold text-[14px]`}>{num(n.sends)}</td>
                        <td className={`${td} text-right text-graphite`}>{pct(n.delivered, n.sends)}</td>
                        <td className={`${td} text-right text-graphite`}>{pct(n.read, n.sends)}</td>
                      </>
                    ) : (
                      <td colSpan={5} className="px-3 py-3 text-right text-[12.5px] text-graphite">
                        {draft ? 'draft · never sent' : 'no sends on the ledger'}
                      </td>
                    )}
                    <td className="pr-3 text-right text-charcoal/25 group-hover:text-forest transition-colors" aria-hidden>›</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Section>
      )}
    </PageFrame>
  );
};

/** A small picture of the message: its header media, the carousel's first
 *  cards side by side, or a plain house-green swatch. */
export const Thumb: React.FC<{ r: Rendered | null }> = ({ r }) => {
  const media = r ? thumbOf(r) : null;
  const cards = r?.cards.filter((c) => c.header_media_url).slice(0, 3) ?? [];
  return (
    <div className="relative flex-none w-[68px] h-[48px] rounded-md overflow-hidden bg-[#2b3a2e]">
      {cards.length > 1 ? (
        <div className="absolute inset-0 flex gap-px">
          {cards.map((c, i) => (
            <div key={i} className="flex-1 min-w-0">
              {c.header_format === 'VIDEO'
                ? <video src={c.header_media_url!} muted playsInline preload="metadata" className="w-full h-full object-cover" />
                : <img src={c.header_media_url!} alt="" loading="lazy" className="w-full h-full object-cover" />}
            </div>
          ))}
        </div>
      ) : media ? (
        media.video
          ? <video src={media.url} muted playsInline preload="metadata" className="w-full h-full object-cover" />
          : <img src={media.url} alt="" loading="lazy" className="w-full h-full object-cover" />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-[#5f8a63] to-[#2f5236]" />
      )}
      {media?.video && cards.length <= 1 && (
        <span className="absolute inset-0 grid place-items-center text-warm-white/90 text-[13px]" aria-hidden>▶</span>
      )}
      {r && r.cards.length > 1 && (
        <span className="absolute right-0.5 bottom-0.5 text-[9px] font-bold text-warm-white bg-charcoal/70 rounded px-1 num">{r.cards.length}</span>
      )}
    </div>
  );
};

export default TemplatesPage;
